import type { ErrorReportInput } from "../../../core/shared/services/error-reporting/error-report.type";
import { detectInAppBrowser } from "./browser-environment.ts";
import type { LoginDiagnosticStage } from "./login-diagnostics.ts";
import { redactLoginDiagnosticText } from "./login-diagnostics.ts";
import type { TrustedDeviceRung } from "./trusted-device-fallback.ts";

const PROBE_STEP_TIMEOUT_MS = 1_500;
const PROBE_DATABASE_NAME = "feather-trusted-device-probe";
const PROBE_STORE_NAME = "probe";
const MAX_ERROR_MESSAGE_LENGTH = 160;
// Chromium incognito profiles get a small fixed quota; a regular profile on a
// device with free space reports far more.
const PRIVATE_MODE_QUOTA_BYTES = 120 * 1024 * 1024;

/**
 * Privacy-safe capability snapshot attached to a trusted-device failure.
 *
 * Every field is a boolean or a short label (`ok`, `failed:DataCloneError`);
 * no key, challenge, token, or identifier is ever included.
 *
 * @example
 * const probe = await collectTrustedDeviceProbe();
 */
export type TrustedDeviceProbe = {
  secureContext: boolean;
  protocol: string;
  subtleCrypto: boolean;
  ecdsaSupport: string;
  indexedDb: string;
  cryptoKeyStorable: string;
  localStorageAvailable: boolean;
  cookiesEnabled: boolean;
  storagePersisted: string;
  inAppBrowser: string;
  privateModeSuspected: boolean;
  clientDomain: string;
};

/**
 * One trusted-device failure to report.
 *
 * @example
 * reportTrustedDeviceFailure({ stage: "trustedDeviceDatabase", error, fallback: "webCryptoExtractable" });
 */
export type TrustedDeviceFailureReport = {
  stage: LoginDiagnosticStage;
  error: unknown;
  /** Rung the ladder moves to next, or `none` when the login is abandoned. */
  fallback?: TrustedDeviceRung | "none";
  timedOut?: boolean;
  timeoutPhase?: string;
  challengeState?: string;
};

type TelemetryDependencies = {
  send: (input: ErrorReportInput) => Promise<void>;
  probe: () => Promise<TrustedDeviceProbe>;
};

type ProbeWindow = {
  isSecureContext?: boolean;
  location?: Pick<Location, "protocol" | "hostname">;
  crypto?: Crypto;
  indexedDB?: IDBFactory;
  localStorage?: Storage;
  navigator?: Navigator;
};

const reportedStages = new Set<string>();
let probePromise: Promise<TrustedDeviceProbe> | undefined;

function errorName(error: unknown): string {
  if (error && typeof error === "object" && "name" in error) {
    const name = (error as { name: unknown }).name;

    if (typeof name === "string" && name) return name.slice(0, 60);
  }

  return "UnknownError";
}

function rootError(error: unknown): unknown {
  let candidate = error;

  for (let depth = 0; depth < 5; depth += 1) {
    if (!(candidate instanceof Error) || candidate.cause === undefined) break;

    candidate = candidate.cause;
  }

  return candidate;
}

function errorMessage(error: unknown): string {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";

  return redactLoginDiagnosticText(message, MAX_ERROR_MESSAGE_LENGTH) ?? "";
}

function withTimeout<TResult>(
  operation: () => Promise<TResult>,
  fallback: TResult,
): Promise<TResult> {
  return new Promise<TResult>((resolve) => {
    const timeoutId = setTimeout(() => resolve(fallback), PROBE_STEP_TIMEOUT_MS);

    operation().then(
      (value) => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      () => {
        clearTimeout(timeoutId);
        resolve(fallback);
      },
    );
  });
}

async function probeEcdsa(
  subtle: SubtleCrypto | undefined,
): Promise<{ result: string; key?: CryptoKey }> {
  if (!subtle) return { result: "skipped" };

  let keyPair: CryptoKeyPair;

  try {
    keyPair = await subtle.generateKey(
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["sign", "verify"],
    );
  } catch (error) {
    return { result: `generateFailed:${errorName(error)}` };
  }

  try {
    await subtle.sign(
      { hash: "SHA-256", name: "ECDSA" },
      keyPair.privateKey,
      new TextEncoder().encode("probe"),
    );
  } catch (error) {
    return { result: `signFailed:${errorName(error)}`, key: keyPair.privateKey };
  }

  return { result: "ok", key: keyPair.privateKey };
}

function probeIndexedDb(
  factory: IDBFactory | undefined,
  key: CryptoKey | undefined,
): Promise<{ indexedDb: string; cryptoKeyStorable: string }> {
  if (!factory || typeof factory.open !== "function") {
    return Promise.resolve({ indexedDb: "missing", cryptoKeyStorable: "skipped" });
  }

  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;

    const cleanUp = (database?: IDBDatabase) => {
      try {
        database?.close();
        factory.deleteDatabase(PROBE_DATABASE_NAME);
      } catch {
        // Cleanup is best effort.
      }
    };

    try {
      request = factory.open(PROBE_DATABASE_NAME, 1);
    } catch (error) {
      resolve({
        indexedDb: `openFailed:${errorName(error)}`,
        cryptoKeyStorable: "skipped",
      });

      return;
    }

    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(PROBE_STORE_NAME)) {
        request.result.createObjectStore(PROBE_STORE_NAME);
      }
    };
    request.onerror = () =>
      resolve({
        indexedDb: `openFailed:${errorName(request.error)}`,
        cryptoKeyStorable: "skipped",
      });
    request.onblocked = () =>
      resolve({ indexedDb: "blocked", cryptoKeyStorable: "skipped" });
    request.onsuccess = () => {
      const database = request.result;

      if (!key) {
        cleanUp(database);
        resolve({ indexedDb: "ok", cryptoKeyStorable: "skipped" });

        return;
      }

      try {
        const transaction = database.transaction(PROBE_STORE_NAME, "readwrite");

        transaction.oncomplete = () => {
          cleanUp(database);
          resolve({ indexedDb: "ok", cryptoKeyStorable: "ok" });
        };
        transaction.onabort = () => {
          cleanUp(database);
          resolve({
            indexedDb: "ok",
            cryptoKeyStorable: `failed:${errorName(transaction.error)}`,
          });
        };
        transaction.objectStore(PROBE_STORE_NAME).put(key, "key");
      } catch (error) {
        cleanUp(database);
        resolve({
          indexedDb: "ok",
          cryptoKeyStorable: `failed:${errorName(error)}`,
        });
      }
    };
  });
}

async function probePersistence(navigatorLike: Navigator | undefined) {
  const storageManager = navigatorLike?.storage;

  if (!storageManager || typeof storageManager.persisted !== "function") {
    return "unsupported";
  }

  return (await storageManager.persisted()) ? "persisted" : "notPersisted";
}

async function probeSmallQuota(navigatorLike: Navigator | undefined) {
  const estimate = navigatorLike?.storage?.estimate;

  if (typeof estimate !== "function") return false;

  const { quota } = await estimate.call(navigatorLike!.storage);

  return typeof quota === "number" && quota > 0 && quota < PRIVATE_MODE_QUOTA_BYTES;
}

function localStorageState(storage: Storage | undefined): {
  available: boolean;
  quotaRejected: boolean;
} {
  if (!storage) return { available: false, quotaRejected: false };

  try {
    storage.setItem("feather-trusted-device-probe", "1");
    storage.removeItem("feather-trusted-device-probe");

    return { available: true, quotaRejected: false };
  } catch (error) {
    return {
      available: false,
      quotaRejected: errorName(error) === "QuotaExceededError",
    };
  }
}

/**
 * Run the bounded capability probes once per page load.
 *
 * @remarks
 * Only called after a failure, never on the happy path. Each step is capped
 * at 1.5s so a hung IndexedDB cannot delay the student's error message.
 *
 * @example
 * const probe = await collectTrustedDeviceProbe(window);
 */
export function collectTrustedDeviceProbe(
  browserWindow: ProbeWindow | undefined = typeof window === "undefined"
    ? undefined
    : (window as unknown as ProbeWindow),
): Promise<TrustedDeviceProbe> {
  if (!probePromise) {
    probePromise = runProbe(browserWindow);
  }

  return probePromise;
}

async function runProbe(
  browserWindow: ProbeWindow | undefined,
): Promise<TrustedDeviceProbe> {
  let storage: Storage | undefined;

  try {
    storage = browserWindow?.localStorage;
  } catch {
    storage = undefined;
  }

  const subtle = browserWindow?.crypto?.subtle;
  const ecdsa = await withTimeout(() => probeEcdsa(subtle), {
    result: "timeout",
  } as { result: string; key?: CryptoKey });
  const database = await withTimeout(
    () => probeIndexedDb(browserWindow?.indexedDB, ecdsa.key),
    { indexedDb: "timeout", cryptoKeyStorable: "timeout" },
  );
  const storagePersisted = await withTimeout(
    () => probePersistence(browserWindow?.navigator),
    "timeout",
  );
  const smallQuota = await withTimeout(
    () => probeSmallQuota(browserWindow?.navigator),
    false,
  );
  const local = localStorageState(storage);

  return {
    secureContext: browserWindow?.isSecureContext === true,
    protocol: browserWindow?.location?.protocol ?? "",
    subtleCrypto: Boolean(subtle),
    ecdsaSupport: ecdsa.result,
    indexedDb: database.indexedDb,
    cryptoKeyStorable: database.cryptoKeyStorable,
    localStorageAvailable: local.available,
    cookiesEnabled: browserWindow?.navigator?.cookieEnabled === true,
    storagePersisted,
    inAppBrowser: detectInAppBrowser(browserWindow?.navigator?.userAgent),
    privateModeSuspected:
      database.indexedDb === "openFailed:InvalidStateError" ||
      local.quotaRejected ||
      smallQuota,
    clientDomain: (browserWindow?.location?.hostname ?? "").slice(0, 120),
  };
}

/**
 * One-line summary of a probe, kept in the report message so it survives
 * even where structured fields are dropped.
 *
 * @example
 * formatTrustedDeviceProbe(probe); // "secure=1 proto=https subtle=1 ecdsa=ok ..."
 */
export function formatTrustedDeviceProbe(probe: TrustedDeviceProbe): string {
  return [
    `secure=${probe.secureContext ? 1 : 0}`,
    `proto=${probe.protocol.replace(":", "")}`,
    `subtle=${probe.subtleCrypto ? 1 : 0}`,
    `ecdsa=${probe.ecdsaSupport}`,
    `idb=${probe.indexedDb}`,
    `keyStore=${probe.cryptoKeyStorable}`,
    `ls=${probe.localStorageAvailable ? 1 : 0}`,
    `cookies=${probe.cookiesEnabled ? 1 : 0}`,
    `persist=${probe.storagePersisted}`,
    `inApp=${probe.inAppBrowser}`,
    `private=${probe.privateModeSuspected ? 1 : 0}`,
  ].join(" ");
}

/**
 * Build the error-log payload for one trusted-device failure.
 *
 * @example
 * const input = buildTrustedDeviceReport(report, probe);
 */
export function buildTrustedDeviceReport(
  report: TrustedDeviceFailureReport,
  probe: TrustedDeviceProbe,
): ErrorReportInput {
  const cause = rootError(report.error);
  const name = errorName(cause);
  const message = errorMessage(cause);
  const fallback = report.fallback ?? "none";

  return {
    message: `Trusted-device ${report.stage} failed (${name}${
      message ? `: ${message}` : ""
    }) -> ${fallback} | ${formatTrustedDeviceProbe(probe)}`,
    source: "trusted-device",
    additionalData: {
      trustedDeviceStage: report.stage,
      trustedDeviceFallback: fallback,
      errorName: name,
      errorMessage: message,
      timedOut: report.timedOut === true,
      timeoutPhase: report.timeoutPhase,
      challengeState: report.challengeState,
      ...probe,
    },
  };
}

/**
 * Report one trusted-device failure to the backend error log.
 *
 * @remarks
 * Throttled to one report per stage per page load, fire-and-forget, and it
 * never throws — telemetry can never break or delay a login.
 *
 * @example
 * void reportTrustedDeviceFailure({ stage: "trustedDeviceCapability", error });
 */
export async function reportTrustedDeviceFailure(
  report: TrustedDeviceFailureReport,
  dependencies: Partial<TelemetryDependencies> = {},
): Promise<void> {
  try {
    if (reportedStages.has(report.stage)) return;

    reportedStages.add(report.stage);

    const probe = await (dependencies.probe ?? collectTrustedDeviceProbe)();
    const send = dependencies.send ?? (await loadDefaultSender());

    await send(buildTrustedDeviceReport(report, probe));
  } catch {
    // Telemetry must never surface to the student.
  }
}

async function loadDefaultSender(): Promise<TelemetryDependencies["send"]> {
  const { reportBrowserError } = await import(
    "../../../core/shared/services/error-reporting/browser-error-reporter.service.ts"
  );

  return reportBrowserError;
}

/** Forget throttling and cached probes (tests only). */
export function resetTrustedDeviceTelemetry(): void {
  reportedStages.clear();
  probePromise = undefined;
}
